import {
  getChequeGiroClearingKey,
  ClearingStatusEnum,
  useDeleteChequeGiroClearing,
} from 'api-hooks/transactions/cheque-giro-clearing';
import { EditSVG, PlusCircleSVG, TrashSVG } from 'common/assets';
import { AuthorizationRules } from 'common/constants';
import { queryClient } from 'common/repositories/query-client';
import stylesColors from 'common/styles/styles.colors';
import notification from 'components/common/notification';
import { Input } from 'components/elements';
import { Button, IconButton } from 'components/elements/buttons';
import Text from 'components/elements/text';
import {
  FullContainer,
  HalfContainer,
  SectionContainer,
  SeparatorVertical,
} from 'components/styles';
import { useAuthorization } from 'hooks/use-authorization';
import useDialog from 'hooks/use-dialog';
import useDrawer, { DrawerProvider } from 'hooks/use-drawer';
import { OneThirdContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { ChequeGiroClearingFormType } from './form-type';
import ClearingFormPanel from './side-panel';
import { ItemContainer } from '../styles';

interface Props {
  chequeNoteId?: string;
}

export default function ClearingSection(props: Props) {
  const { t } = useTranslation();
  const { control } = useFormContext<ChequeGiroClearingFormType>();
  const dialog = useDialog();
  const { fields } = useFieldArray({
    control,
    name: 'chequeNoteClearings',
    keyName: 'customId',
  });

  const { mutateAsync } = useDeleteChequeGiroClearing();

  const drawer = useDrawer();

  const { can } = useAuthorization();

  const onDelete = React.useCallback(
    async (chequeGiroClearingId?: string) => {
      dialog.showConfirmation({
        message: t('common:confirmation_delete_text'),
        title: t('common:confirmation'),
        onPositiveAction: async (dismiss) => {
          try {
            dismiss();
            await mutateAsync({ chequeGiroClearingId });
            queryClient.refetchQueries([getChequeGiroClearingKey()[0]]);
            dismiss();
            notification.success({ message: t('common:successfully_deleted') });
          } catch (error) {
            notification.error({ message: error.message });
          }
        },
        onNegativeAction: (dismiss) => {
          dismiss();
        },
      });
    },
    [dialog, t, mutateAsync],
  );

  return (
    <FullContainer>
      <SectionContainer>
        <Text variant="HeadingXSmall">{t('common:clearing')}</Text>
        {can(AuthorizationRules.ChequeNotesStoreClearing) && (
          <Button
            buttonType="tertiary"
            onClick={() => {
              drawer.showCustom({
                desktopDrawerWidthPercentage: 50,
                render: (onClose) => (
                  <DrawerProvider>
                    <ClearingFormPanel
                      chequeNotesId={props.chequeNoteId}
                      onClose={onClose}
                    />
                  </DrawerProvider>
                ),
              });
            }}
            startEnhancer={(color, size) => (
              <PlusCircleSVG color={color} size={size} />
            )}
          >
            {t('common:add')}
          </Button>
        )}
      </SectionContainer>

      <SeparatorVertical $margin={16} />
      {!!fields.length && (
        <SectionContainer>
          <HalfContainer />

          <div style={{ width: 30 }} />
        </SectionContainer>
      )}
      {fields.map((field, idx) => (
        <ItemContainer key={field.customId} $style={{ alignItems: 'center' }}>
          <OneThirdContainer>
            <Input
              type="select"
              name={`chequeNoteClearings[${idx}].status`}
              placeholder={t('common:status')}
              clearable={false}
              label={t('common:status')}
              options={[
                {
                  id: ClearingStatusEnum.cleared,
                  label: t(`common:${ClearingStatusEnum.cleared}`),
                },
                {
                  id: ClearingStatusEnum.rejected,
                  label: t(`common:${ClearingStatusEnum.rejected}`),
                },
              ]}
              required
              hideRequiredStar
              disabled={idx !== fields.length - 1}
            />
          </OneThirdContainer>
          <OneThirdContainer>
            <Input
              type="date-time"
              name={`chequeNoteClearings[${idx}].clearingAt`}
              placeholder={t('common:cleared_at')}
              label={t('common:cleared_at')}
              clearable={false}
              required
              hideRequiredStar
              disabled={idx !== fields.length - 1}
            />
          </OneThirdContainer>
          <OneThirdContainer>
            <Input
              type="text"
              name={`chequeNoteClearings[${idx}].createdBy`}
              placeholder={t('common:created_by')}
              label={t('common:created_by')}
              clearable={false}
              hideRequiredStar
              disabled
            />
          </OneThirdContainer>
          {idx === fields.length - 1 && (
            <ItemContainer>
              {can(AuthorizationRules.ChequeNotesUpdateClearing) && (
                <IconButton
                  onClick={() => {
                    drawer.showCustom({
                      desktopDrawerWidthPercentage: 50,
                      render: (onClose) => (
                        <DrawerProvider>
                          <ClearingFormPanel data={field} onClose={onClose} />
                        </DrawerProvider>
                      ),
                    });
                  }}
                  buttonType="tertiary"
                >
                  <EditSVG width="24" color={stylesColors.blueDefault} />
                </IconButton>
              )}
              {can(AuthorizationRules.ChequeNotesDeleteClearing) && (
                <IconButton buttonType="tertiary">
                  <TrashSVG
                    color={stylesColors.redDefault}
                    width="24"
                    onClick={() => onDelete(field.id)}
                  />
                </IconButton>
              )}
            </ItemContainer>
          )}
        </ItemContainer>
      ))}
    </FullContainer>
  );
}
